// Always in English — a technical data file for spreadsheet analysis, not a
// translated UI string, same convention as `serializeInp.ts`'s own .inp
// export (CLAUDE.md: SWMM's own domain vocabulary stays in plain English).

import type { SimulationSummary } from "../../engine/simulationSummary.ts";
import { unitSystemOf } from "../units.ts";

function csvField(value: string | number): string {
  let s = String(value);
  // CSV formula injection guard: element names (topNodeDepths/topLinkFlows)
  // come straight from an untrusted .inp file — a name like
  // `=HYPERLINK(...)` would execute as a spreadsheet formula when this CSV
  // is later opened in Excel/Sheets with default settings. A leading `'`
  // (Excel/Sheets' own "treat as text" marker) neutralizes it while keeping
  // the value readable, same as every other spreadsheet-export mitigation
  // for this class of issue.
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function csvRow(fields: (string | number)[]): string {
  return fields.map(csvField).join(",");
}

/** Plain-text CSV — `\r\n` line endings (RFC 4180), always ending with one. */
export function buildSimulationSummaryCsv(summary: SimulationSummary, flowUnits: string): string {
  const sys = unitSystemOf(flowUnits);
  const lengthUnit = sys === "US" ? "ft" : "m";
  const rainUnit = sys === "US" ? "in" : "mm";

  const rows: (string | number)[][] = [["Section", "Metric", "Value", "Unit"]];

  const c = summary.runoffContinuity;
  if (c) {
    if (c.hasInitLidStorage)
      rows.push([
        "Runoff Continuity",
        "Initial LID Storage",
        c.initLidStorageDepth.toFixed(3),
        rainUnit,
      ]);
    rows.push([
      "Runoff Continuity",
      "Total Precipitation",
      c.totalPrecipDepth.toFixed(3),
      rainUnit,
    ]);
    if (c.hasOutfallRunon)
      rows.push(["Runoff Continuity", "Outfall Runon", c.totalRunonDepth.toFixed(3), rainUnit]);
    rows.push(["Runoff Continuity", "Evaporation Loss", c.totalEvapDepth.toFixed(3), rainUnit]);
    rows.push(["Runoff Continuity", "Infiltration Loss", c.totalInfilDepth.toFixed(3), rainUnit]);
    rows.push(["Runoff Continuity", "Surface Runoff", c.totalRunoffDepth.toFixed(3), rainUnit]);
    if (c.hasLidDrainage)
      rows.push(["Runoff Continuity", "LID Drainage", c.lidDrainageDepth.toFixed(3), rainUnit]);
    rows.push(["Runoff Continuity", "Final Storage", c.finalStorageDepth.toFixed(3), rainUnit]);
    rows.push([
      "Runoff Continuity",
      "Continuity Error",
      c.continuityErrorPct !== null ? c.continuityErrorPct.toFixed(3) : "",
      "%",
    ]);
  }

  for (const e of summary.topNodeDepths) {
    rows.push(["Top Node Depths", e.name, e.value.toFixed(3), lengthUnit]);
  }
  for (const e of summary.topLinkFlows) {
    rows.push(["Top Link Flows", e.name, e.value.toFixed(3), flowUnits]);
  }
  if (summary.peakSystemOutflow !== null) {
    rows.push(["System", "Peak System Outflow", summary.peakSystemOutflow.toFixed(3), flowUnits]);
  }

  return rows.map(csvRow).join("\r\n") + "\r\n";
}
